/**
 * A stand-in car body at the TPDM design-vehicle dimensions
 * (4.6 x 1.7 x 1.5 m, TPDM V2 Table 2.2.2.1).
 *
 * Only visible in chase view, so it is deliberately cheap. Its real job is to
 * make the scale of the road read correctly: at the right dimensions on a
 * 7.3 m carriageway, the lane looks like a Hong Kong lane.
 */
import { BoxGeometry, CircleGeometry, CylinderGeometry, Group, Mesh, Object3D } from 'three';
import { CAR_GEOMETRY, VEHICLE } from '../core/spec';
import type { Materials } from '../scene/materials';
import type { CarState } from './arcadeCar';

export const CAR_MODEL_DIMENSIONS = CAR_GEOMETRY;

/** Protect the exact regression where visual wheels exceeded the TPDM envelope. */
export function assertCarModelDimensions(): void {
  const outerWheelEdge = CAR_GEOMETRY.TRACK_M / 2 + CAR_GEOMETRY.WHEEL_WIDTH_M / 2;
  if (outerWheelEdge > VEHICLE.WIDTH_M / 2 + 1e-9) {
    throw new Error(`wheel outer edge ${outerWheelEdge} m exceeds ${VEHICLE.WIDTH_M / 2} m vehicle half-width`);
  }
  if (outerWheelEdge <= CAR_GEOMETRY.BODY_WIDTH_M / 2) {
    throw new Error('wheels must visibly protrude beyond the body');
  }
}

export interface CarModel {
  root: Object3D;
  sync(car: CarState): void;
  setVisible(v: boolean): void;
  setContactShadow(v: boolean): void;
}

export function createCarModel(materials: Materials): CarModel {
  assertCarModelDimensions();
  const root = new Group();
  root.name = 'car';
  const visibleModel = new Group();
  root.add(visibleModel);

  const contactShadow = new Mesh(new CircleGeometry(1, 24), materials.contactShadow);
  contactShadow.rotation.x = -Math.PI / 2;
  contactShadow.scale.set(VEHICLE.WIDTH_M * 0.65, VEHICLE.LENGTH_M * 0.36, 1);
  contactShadow.position.y = 0.018;
  contactShadow.renderOrder = 2;
  root.add(contactShadow);

  const bodyH = 0.46;
  const body = new Mesh(
    new BoxGeometry(CAR_GEOMETRY.BODY_WIDTH_M, bodyH, VEHICLE.LENGTH_M * 0.94),
    materials.carBody,
  );
  body.position.y = CAR_GEOMETRY.FLOOR_HEIGHT_M + bodyH / 2;
  body.castShadow = true;
  visibleModel.add(body);

  const cabin = new Mesh(
    new BoxGeometry(CAR_GEOMETRY.BODY_WIDTH_M * 0.9, 0.57, VEHICLE.LENGTH_M * 0.46),
    materials.glass,
  );
  cabin.position.set(0, 1.21, -0.12);
  cabin.castShadow = true;
  visibleModel.add(cabin);

  // Separate bonnet/boot planes read as a car at chase-camera distance, where
  // an undifferentiated box reads as a toy brick.
  const panelGeo = new BoxGeometry(CAR_GEOMETRY.BODY_WIDTH_M * 0.92, 0.018, 0.025);
  for (const z of [-0.72, 0.85]) {
    const panel = new Mesh(panelGeo, materials.tyre);
    panel.position.set(0, CAR_GEOMETRY.FLOOR_HEIGHT_M + bodyH + 0.012, z);
    visibleModel.add(panel);
  }
  const bumperGeo = new BoxGeometry(CAR_GEOMETRY.BODY_WIDTH_M * 1.03, 0.16, 0.12);
  for (const z of [-VEHICLE.LENGTH_M * 0.48, VEHICLE.LENGTH_M * 0.48]) {
    const bumper = new Mesh(bumperGeo, materials.tyre);
    bumper.position.set(0, CAR_GEOMETRY.FLOOR_HEIGHT_M + 0.16, z);
    visibleModel.add(bumper);
  }
  const lampGeo = new BoxGeometry(0.34, 0.13, 0.025);
  for (const side of [-1, 1]) {
    const headlight = new Mesh(lampGeo, materials.marking);
    headlight.position.set(side * 0.43, CAR_GEOMETRY.FLOOR_HEIGHT_M + 0.31, VEHICLE.LENGTH_M * 0.475);
    visibleModel.add(headlight);
    const tailLight = new Mesh(lampGeo, materials.signalRed);
    tailLight.position.set(side * 0.43, CAR_GEOMETRY.FLOOR_HEIGHT_M + 0.31, -VEHICLE.LENGTH_M * 0.475);
    visibleModel.add(tailLight);
  }

  const wheelGeo = new CylinderGeometry(
    CAR_GEOMETRY.WHEEL_RADIUS_M,
    CAR_GEOMETRY.WHEEL_RADIUS_M,
    CAR_GEOMETRY.WHEEL_WIDTH_M,
    14,
  );
  wheelGeo.rotateZ(Math.PI / 2);

  const steered: Object3D[] = [];
  const halfWb = VEHICLE.WHEELBASE_M / 2;
  for (const [fwd, right] of [
    [halfWb, -CAR_GEOMETRY.TRACK_M / 2],
    [halfWb, CAR_GEOMETRY.TRACK_M / 2],
    [-halfWb, -CAR_GEOMETRY.TRACK_M / 2],
    [-halfWb, CAR_GEOMETRY.TRACK_M / 2],
  ] as const) {
    const pivot = new Object3D();
    // Model space: +Z is forward (matched to the root's yaw convention below).
    pivot.position.set(right, CAR_GEOMETRY.WHEEL_RADIUS_M, fwd);
    const wheel = new Mesh(wheelGeo, materials.tyre);
    wheel.castShadow = true;
    pivot.add(wheel);
    visibleModel.add(pivot);
    if (fwd > 0) steered.push(pivot);
  }

  return {
    root,
    sync(car: CarState) {
      root.position.set(car.x, car.y, car.z);
      // heading 0 faces -Z, and the model's +Z is forward, so add PI.
      root.rotation.set(0, car.heading + Math.PI, 0, 'YXZ');
      root.rotateX(car.pitch);
      root.rotateZ(car.roll);
      for (const p of steered) p.rotation.y = -car.steerAngle;
    },
    setVisible(v: boolean) {
      visibleModel.visible = v;
    },
    setContactShadow(v: boolean) {
      contactShadow.visible = v;
    },
  };
}
